import React, { useState, useEffect, useMemo } from 'react';
import {
  Box,
  Stack,
  Checkbox,
  CheckboxGroup,
  FormControl,
  FormLabel,
  Button,
  Flex,
  Skeleton,
  Tooltip,
} from '@chakra-ui/react';
import { useReactiveVar } from '@apollo/client';

import useApiClientsLists, {
  ApiClientsLists,
  apiClientsListsVar,
} from 'sharedHooks/useApiClientsLists';
import type { Client } from 'generated/graphql';
import type { ClientFilter } from '../types/clientTypes';
import { allClientsListVar } from '../graphql/apolloCache';

interface FilterChoice {
  displayName: string;
  value: string | number;
}

interface Filter {
  field: keyof ClientFilter;
  values: Array<string | number>;
  api?: boolean;
}

interface ApiFilterChoice extends FilterChoice {
  value: keyof ApiClientsLists;
}

interface FilterGroupProps {
  field: keyof ClientFilter;
  name: string;
  id: string;
  choices: FilterChoice[];
  apiChoices: ApiFilterChoice[];
  updateFilters: (newFilter: Filter) => void;
  defaultValue: string[] | number[];
}

function FilterGroup(props: FilterGroupProps): JSX.Element {
  const {
    field,
    name,
    id,
    choices,
    apiChoices,
    updateFilters,
    defaultValue,
  } = props;

  function isClientActive(client: Client): boolean {
    const { status = (client as any).status } = client as any;
    if (typeof status === 'string') return status.toLowerCase() === 'active';

    const { closed } = client as any;
    const { inCustody } = client as any;
    if (typeof closed === 'boolean' || typeof inCustody === 'boolean') {
      return !closed && !inCustody;
    }
    return false;
  }

  function countActiveClients(list: Client[]): number {
    return list.reduce((sum, c) => sum + (isClientActive(c) ? 1 : 0), 0);
  }

  const [groupValues, setGroupValues] = useState<string[] | number[]>([]);
  const [groupApiValues, setGroupApiValues] = useState<string[] | number[]>([]);

  const apiClientsLists = useReactiveVar(apiClientsListsVar);
  const allClients = useReactiveVar(allClientsListVar) as Client[];

  const activeTotal = useMemo(() => countActiveClients(allClients), [
    allClients,
  ]);

  useApiClientsLists(apiChoices.map((choice) => choice.value));

  const onFilterChange = (values: string[] | number[]): void => {
    setGroupValues(values);
    updateFilters({ field, values });
  };

  const onApiFilterChange = (values: string[] | number[]): void => {
    setGroupApiValues(values);
    updateFilters({ field, values, api: true });
  };

  useEffect(() => {
    onFilterChange(defaultValue);
  }, []);

  if (!choices.length) {
    return <></>;
  }

  const hasAnySelection = groupValues.length > 0 || groupApiValues.length > 0;

  return (
    <Box>
      <form
        onReset={() => {
          onFilterChange([]);
          onApiFilterChange([]);
        }}
        id={`${id}`}
      >
        <FormControl as="fieldset">
          <Flex direction="row" justifyContent="space-between">
            <FormLabel
              as="legend"
              textTransform="uppercase"
              fontSize="sm"
              fontWeight="700"
              color="brand.gray1"
            >
              {name}
            </FormLabel>
            <Button
              size="xs"
              variant="ghost"
              color="brand.blue2"
              visibility={hasAnySelection ? 'visible' : 'hidden'}
              type="reset"
              form={`${id}`}
            >
              Clear
            </Button>
          </Flex>
          <CheckboxGroup onChange={onFilterChange} value={groupValues}>
            <Stack>
              {choices.map((choice) => {
                const isStatusActiveOption =
                  field === 'status' &&
                  String(choice.value).toLowerCase() === 'active';

                return (
                  <Checkbox
                    key={choice.value}
                    value={choice.value}
                    color="brand.gray1"
                    borderColor="brand.gray1"
                    display="flex"
                    alignItems="start"
                    sx={{ '.chakra-checkbox__control': { marginTop: '.25em' } }}
                  >
                    {choice.displayName}
                    {isStatusActiveOption && (
                      <Box as="span"> ({activeTotal})</Box>
                    )}
                  </Checkbox>
                );
              })}
            </Stack>
          </CheckboxGroup>

          <CheckboxGroup onChange={onApiFilterChange} value={groupApiValues}>
            <Stack mt={2}>
              {apiChoices.map((choice) => (
                <Skeleton
                  key={choice.value}
                  isLoaded={apiClientsLists[choice.value].loaded}
                  data-testid="filter-group-skeleton"
                >
                  <Checkbox
                    isDisabled={!!apiClientsLists[choice.value].error}
                    value={choice.value}
                    color="brand.gray1"
                    borderColor="brand.gray1"
                    display="flex"
                    alignItems="start"
                    sx={{ '.chakra-checkbox__control': { marginTop: '.25em' } }}
                  >
                    <Tooltip
                      hasArrow
                      placement="bottom-start"
                      isDisabled={!apiClientsLists[choice.value].error}
                      label="There was an issue loading this filter."
                      bg="brand.gray7"
                      color="brand.gray2"
                    >
                      {choice.displayName}
                    </Tooltip>
                  </Checkbox>
                </Skeleton>
              ))}
            </Stack>
          </CheckboxGroup>
        </FormControl>
      </form>
    </Box>
  );
}

export default React.memo(FilterGroup);
